import {
    Fragment,
    MouseEventHandler,
    useEffect,
    useRef,
    useState,
} from "react";
import { Transition } from "@headlessui/react";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";
import { flushSync } from "react-dom";
import { mergeRefs } from "react-merge-refs";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import cx from "@hsl/core/utils/cx";
import useFCSubscriptionStore from "@hsl/fc-subscription/store";
import {
    type FundDataWithType,
    type ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import useFundData from "../../../hooks/useFundData";
import useFundPageURL from "../../../hooks/useFundPageURL";
import { Translate } from "../../../hooks/useTranslation";
import { type SubscribableAsset } from "../../../openapi";
import { generateLocalId } from "../../../store/helpers";
import useExplorerStore from "../../../store/store";
import Button from "../../Button";
import { FundCardModal } from "../../FundCard/components/FundCardModal";
import FundCardTabs from "../../FundCard/components/FundCardTabs";
import { FundCardTitle } from "../../FundCard/components/FundCardTitle";
import FundSummary from "../../FundCard/components/FundSummary";
import { AboutView, TagsView } from "../../FundCard/views";
import ShareIcon from "../../icons/Share";
import SubscriptionButtons from "../SubscriptionButtons";

interface Props {
    fundId: string;
    subscriptions: SubscribableAsset[];
    index: number;
}

export const SquareFundCard = ({ subscriptions, fundId, index }: Props) => {
    const { buttonClickEvent } = useYardActionEvents();
    const fund = useFundData(`fund-${fundId}`);
    const ref = useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = useState(false);
    const breakpoint = useBreakpointContext();
    const isUpdated = useFCSubscriptionStore((state) => state.isUpdated);
    const [showOverlay, setShowOverlay] = useState<
        "subscribe" | "watch" | null
    >(null);

    if (!fund) {
        console.error(`Fund ${fundId} not found`);
        return null;
    }

    let alignment: "left" | "right" | "centre" = "left";

    switch (breakpoint?.currentBreakpoint) {
        case "md":
            if (index % 3 === 1) alignment = "centre";
            if (index % 3 === 2) alignment = "right";
            break;
        case "lg":
        case "xl":
        case "2xl":
            if (index % 4 === 2 || index % 4 == 3) alignment = "right";
            break;
    }

    // TODO: FETCH MESSAGES
    const renderMessages = () => (
        <>
            {/* {messages.map((message, i) => (
                <InfoMessage
                    key={message.severity}
                    className={i === 0 ? "mt-4" : "mt-2"}
                    severity={message.severity}
                    html={message.data}
                />
            ))} */}
        </>
    );
    const subsOfType = (type: "FULL" | "WATCHLIST") =>
        subscriptions.filter((sub) => sub.subscriptionType === type);
    const isSubscribed = subsOfType("FULL").length > 0;
    const isWatching = subsOfType("WATCHLIST").length > 0;
    const hasUpdates = isUpdated(fundId);

    const isSmall = breakpoint?.currentBreakpoint === "xs";
    const handleTitleClick: MouseEventHandler = (e) => {
        e.stopPropagation();
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            url.pathname = fund.data.data.fundPageURL;
            window.location.href = url.href;
        }
    };

    return (
        <div className="relative">
            <button
                onClick={() => {
                    setExpanded((e) => !e);
                    buttonClickEvent({
                        name: "View details",
                        position: "Fund details",
                        details: fund.data.data.name,
                        fund: {
                            id: fund.data.id,
                            type: fund.type,
                            name: fund.data.data.name,
                        },
                    });
                    flushSync(() => {
                        setTimeout(() => {
                            ref.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                                inline: "nearest",
                            });
                        }, 200);
                    });
                }}
                className={cx(
                    "hover:ring-core-blue flex min-h-[10rem] w-full flex-col bg-white shadow ring-1 ring-black/5 transition-shadow hover:shadow-lg sm:min-h-[14rem]",
                    expanded && "ring-core-blue hover:ring-core-blue ring-2",
                )}
            >
                <div className="flex flex-1 flex-col items-stretch p-4">
                    <div className="flex shrink-0 items-center justify-between">
                        <TagsView fund={fund} stackedPMCTags />
                        <SubscriptionButtons
                            fund={fund}
                            isSubscribed={isSubscribed}
                            isWatching={isWatching}
                            ignoreBasket
                            hasUpdates={hasUpdates}
                            onClickSubscribe={() => setShowOverlay("subscribe")}
                            onClickWatch={() => setShowOverlay("watch")}
                        />
                    </div>
                    <FundCardTitle
                        className="mb-4 mt-8 underline-offset-2 hover:underline"
                        onClick={handleTitleClick}
                    >
                        {fund.data.data.name}
                    </FundCardTitle>
                </div>
                <div className="border-platinum grid h-8 w-full shrink-0 place-items-center border-t">
                    <ChevronDownIcon
                        className={cx(
                            "h-6 w-6 transition-transform",
                            expanded && "rotate-180",
                        )}
                    />
                </div>
                <div
                    // arrow
                    aria-hidden="true"
                    className={cx(
                        "absolute left-1/2 top-full z-10 h-0 w-0 -translate-x-1/2 translate-y-44 rotate-45 transform border-[12px] border-b-transparent border-l-white border-r-transparent border-t-white opacity-0 transition-all",
                        "z-[100]",
                        expanded && "translate-y-2 opacity-100",
                    )}
                />
            </button>
            <SquareCardOverlay
                fund={fund}
                show={showOverlay}
                onClose={() => setShowOverlay(null)}
                hasUpdates={hasUpdates}
                viewDocuments={() => {
                    setShowOverlay(null);
                    setExpanded(true);
                }}
            />
            <FundDetailsDropdown
                cardRef={ref}
                fund={fund}
                show={expanded && !isSmall}
                setShow={setExpanded}
                alignment={alignment}
                renderMessages={renderMessages}
            />
            <FundCardModal
                show={isSmall && expanded}
                onClose={() => isSmall && setExpanded(false)}
                fund={fund}
                renderMessages={renderMessages}
                hasUpdates={hasUpdates}
                initialTab="Key documents"
            />
        </div>
    );
};

const SquareCardOverlay = ({
    fund,
    show,
    onClose,
    hasUpdates,
    viewDocuments,
}: {
    fund: FundDataWithType;
    show: "subscribe" | "watch" | null;
    onClose: () => void;
    hasUpdates: boolean;
    viewDocuments: () => void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const toggleBasketItem = useFCSubscriptionStore(
        (state) => state.toggleBasketItem,
    );
    const getShareClassById = useExplorerStore(
        (state) => state.getShareClassById,
    );

    const scId = generateLocalId("shareclass");
    const shareClasses = fund.data.share_classes
        .map((id) => getShareClassById(scId(id)))
        .filter((sc): sc is ShareClassDataWithType => Boolean(sc));

    let subscriptionUrl = "/my-funds";

    switch (show) {
        case "watch":
            subscriptionUrl += "/watch";
            break;
        case "subscribe":
            subscriptionUrl += "/subscribe";
            break;
    }

    useEffect(() => {
        if (show) {
            const closeOutside = (evt: MouseEvent) => {
                if (!ref.current?.contains(evt.target as Node)) {
                    onClose();
                }
            };
            document.addEventListener("mousedown", closeOutside);
            return () =>
                document.removeEventListener("mousedown", closeOutside);
        }
    }, [onClose, show]);

    return (
        <Transition
            show={show !== null}
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div
                className="bg-charcoal/30 absolute inset-0 z-40 grid cursor-default place-items-center overflow-visible p-2 max-sm:p-2"
                onClick={(evt) => {
                    evt.stopPropagation();
                    onClose();
                }}
            >
                <div
                    ref={ref}
                    className="flex w-full flex-col gap-2 rounded bg-white p-3 shadow-lg max-sm:h-full sm:max-w-[18rem]"
                    onClick={(evt) => {
                        evt.stopPropagation();
                    }}
                >
                    <div className="flex flex-col gap-3">
                        {hasUpdates && (
                            <Button
                                color="primary"
                                className="w-full font-normal"
                                as="button"
                                onClick={() => {
                                    viewDocuments();
                                }}
                            >
                                <Translate>View documents</Translate>
                            </Button>
                        )}
                        <Button
                            color={hasUpdates ? "white" : "primary"}
                            className="w-full font-normal"
                            as="link"
                            variant="large"
                            to="/my-funds/manage"
                        >
                            <Translate>Manage your funds</Translate>
                        </Button>
                        <Button
                            color="white"
                            className="w-full font-normal"
                            as="link"
                            variant="large"
                            to={subscriptionUrl + "/share"}
                            onClick={() => {
                                toggleBasketItem(fund, shareClasses);
                            }}
                            stripSearchParams={["step"]}
                            icon={<ShareIcon className="h-4 w-4 text-white" />}
                        >
                            <Translate>Share</Translate>
                        </Button>
                    </div>
                </div>
            </div>
        </Transition>
    );
};

const FundDetailsDropdown = ({
    fund,
    show,
    setShow,
    alignment,
    renderMessages,
    cardRef,
}: {
    fund: FundDataWithType;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    alignment: "left" | "right" | "centre";
    renderMessages: () => React.ReactNode;
    cardRef: React.RefObject<HTMLDivElement>;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const url = useFundPageURL(fund.data.data.fundPageURL);
    const markRead = useFCSubscriptionStore((state) => state.markRead);

    useEffect(() => {
        if (show) {
            const handleClickOutside = (evt: MouseEvent) => {
                if (ref.current && !ref.current.contains(evt.target as Node)) {
                    setShow(false);
                }
            };
            document.addEventListener("click", handleClickOutside);
            return () =>
                document.removeEventListener("click", handleClickOutside);
        }
    }, [show, setShow]);

    const hasUpdates = useFCSubscriptionStore((state) => state.isUpdated);

    return (
        <Transition
            show={show}
            className={cx(
                "absolute top-full z-[55] hidden translate-y-4 sm:block",
                alignment === "left"
                    ? "left-0"
                    : alignment === "centre"
                      ? "left-1/2 -translate-x-1/2"
                      : "right-0",
            )}
            enter="transition ease-out duration-100 transform"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transition ease-in duration-75 transform"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
        >
            <div
                ref={mergeRefs([ref, cardRef])}
                className="w-[800px] max-w-[80vw] bg-white p-4 ring-1 ring-black/5 drop-shadow-2xl"
            >
                <div className="items-top flex w-full justify-between gap-x-6">
                    <FundCardTitle>
                        {url ? (
                            <a href={url} className="hover:underline">
                                {fund.data.data.name}
                            </a>
                        ) : (
                            fund.data.data.name
                        )}
                    </FundCardTitle>
                    <TagsView fund={fund} stackedPMCTags />
                </div>
                <AboutView fund={fund} />
                <FundSummary fund={fund} tabs />
                {renderMessages()}
                <div className="pt-4">
                    <FundCardTabs
                        fund={fund}
                        type="pill"
                        initialTab="documents"
                        docHasUpdates={hasUpdates}
                        markRead={markRead}
                    />
                </div>
            </div>
        </Transition>
    );
};
